package org.teddapp.project.portfolio

import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

/** Lien externe d'une pièce de portfolio : `portfolio_piece.links` = JSON de [PieceLink]. */
@Serializable
data class PieceLink(val label: String = "", val url: String)

/** Image par URL d'une pièce de portfolio : `portfolio_piece.images` = JSON de [PieceImage]. */
@Serializable
data class PieceImage(val url: String, val caption: String = "")

/** Statut d'une pièce, tel que stocké dans `portfolio_piece.status`. */
object PieceStatus {
    const val DRAFT = "draft"
    const val PUBLISHED = "published"
}

/**
 * Encodage/décodage JSON des listes de liens et d'images. Tolérant en lecture :
 * une valeur nulle, vide ou mal formée devient une liste vide, jamais une exception.
 */
object PortfolioCodecs {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true; encodeDefaults = true }

    fun decodeLinks(raw: String?): List<PieceLink> = decode(raw, ListSerializer(PieceLink.serializer()))
    fun decodeImages(raw: String?): List<PieceImage> = decode(raw, ListSerializer(PieceImage.serializer()))

    fun encodeLinks(links: List<PieceLink>): String? =
        links.filter { it.url.isNotBlank() }.takeIf { it.isNotEmpty() }
            ?.let { json.encodeToString(ListSerializer(PieceLink.serializer()), it) }

    fun encodeImages(images: List<PieceImage>): String? =
        images.filter { it.url.isNotBlank() }.takeIf { it.isNotEmpty() }
            ?.let { json.encodeToString(ListSerializer(PieceImage.serializer()), it) }

    private fun <T> decode(raw: String?, serializer: kotlinx.serialization.KSerializer<List<T>>): List<T> {
        if (raw.isNullOrBlank()) return emptyList()
        return try {
            json.decodeFromString(serializer, raw)
        } catch (_: Throwable) {
            emptyList()
        }
    }
}
